import { useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cloud, Watch, Printer, CheckCircle2 } from 'lucide-react';

interface RewardSimulationProps {
    type: 'sync' | 'print' | null;
    onComplete: () => void;
}

// Pre-generate random values for particles to avoid Math.random() during render
// This ensures render purity while maintaining visual variety
interface ParticleConfig {
    initialX: number;
    initialY: number;
    animateY: number;
    duration: number;
    delay: number;
}

function generateParticleConfigs(count: number): ParticleConfig[] {
    return Array.from({ length: count }, () => ({
        initialX: Math.random() * 400 - 200,
        initialY: Math.random() * 400 - 200,
        animateY: Math.random() * -600,
        duration: 2 + Math.random() * 2,
        delay: Math.random() * 2
    }));
}

// Static particle configs - generated once at module load
const PARTICLE_CONFIGS = generateParticleConfigs(10);

export function RewardSimulation({ type, onComplete }: RewardSimulationProps) {
    useEffect(() => {
        if (type) {
            const timer = setTimeout(onComplete, 3500);
            return () => clearTimeout(timer);
        }
    }, [type, onComplete]);

    // Memoize particle configs to maintain stability across renders
    const particleConfigs = useMemo(() => PARTICLE_CONFIGS, []);

    return (
        <AnimatePresence>
            {type && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[200] flex items-center justify-center bg-zinc-950/90 backdrop-blur-xl"
                >
                    <div className="relative w-full max-w-lg aspect-square flex flex-col items-center justify-center text-center p-12">
                        {/* Background Pulse */}
                        <motion.div
                            animate={{
                                scale: [1, 1.2, 1],
                                opacity: [0.1, 0.2, 0.1]
                            }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="absolute inset-0 bg-blue-500 rounded-full blur-[100px]"
                        />

                        {type === 'sync' && (
                            <div className="relative z-10 space-y-8">
                                <div className="flex items-center justify-center gap-12">
                                    <motion.div
                                        initial={{ x: -50, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        transition={{ delay: 0.2 }}
                                        className="p-6 bg-white/10 rounded-3xl border border-white/20 backdrop-blur-md"
                                    >
                                        <Cloud className="w-12 h-12 text-blue-400" />
                                    </motion.div>

                                    {/* Flow Animation */}
                                    <div className="relative w-24 h-2">
                                        <div className="absolute inset-0 bg-white/10 rounded-full" />
                                        <motion.div
                                            initial={{ left: 0 }}
                                            animate={{ left: '100%' }}
                                            transition={{ duration: 0.8, repeat: 3, ease: "linear" }}
                                            className="absolute top-0 w-8 h-full bg-blue-500 rounded-full blur-sm"
                                        />
                                        <motion.div
                                            initial={{ left: 0 }}
                                            animate={{ left: '100%' }}
                                            transition={{ duration: 0.8, repeat: 3, ease: "linear", delay: 0.4 }}
                                            className="absolute top-0 w-4 h-full bg-white rounded-full"
                                        />
                                    </div>

                                    <motion.div
                                        initial={{ x: 50, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        transition={{ delay: 0.6 }}
                                        className="p-6 bg-white/10 rounded-3xl border border-white/20 backdrop-blur-md"
                                    >
                                        <Watch className="w-12 h-12 text-white" />
                                    </motion.div>
                                </div>
                                <motion.div
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 1 }}
                                    className="space-y-4"
                                >
                                    <h2 className="text-3xl font-black text-white tracking-tight">Sync Established</h2>
                                    <p className="text-blue-200 text-lg">Your zones are synced with Intervals.icu.</p>
                                    <div className="flex items-center justify-center gap-2 text-green-400 font-bold">
                                        <CheckCircle2 className="w-5 h-5" />
                                        <span>Intervals.icu Active</span>
                                    </div>
                                </motion.div>
                            </div>
                        )}

                        {type === 'print' && (
                            <div className="relative z-10 space-y-8">
                                <div className="flex items-center justify-center">
                                    <motion.div
                                        initial={{ rotateY: -90, opacity: 0 }}
                                        animate={{ rotateY: 0, opacity: 1 }}
                                        transition={{ type: 'spring', damping: 12 }}
                                        className="relative w-48 h-64 bg-white rounded-lg shadow-2xl flex flex-col p-4 overflow-hidden"
                                    >
                                        {/* Simplified Plan Preview */}
                                        <div className="w-full h-4 bg-slate-100 rounded mb-4" />
                                        <div className="grid grid-cols-2 gap-2 mb-4">
                                            <div className="h-2 bg-slate-50 rounded" />
                                            <div className="h-2 bg-slate-50 rounded" />
                                        </div>
                                        <div className="space-y-2">
                                            {[...Array(8)].map((_, i) => (
                                                <div key={i} className="h-1.5 bg-slate-50 rounded w-full" />
                                            ))}
                                        </div>
                                        {/* Paper Unfold Effect */}
                                        <motion.div
                                            initial={{ top: '100%' }}
                                            animate={{ top: 0 }}
                                            transition={{ duration: 1.5, ease: "easeOut" }}
                                            className="absolute inset-0 bg-blue-500/5 mix-blend-multiply"
                                        />
                                    </motion.div>
                                </div>
                                <motion.div
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 1 }}
                                    className="space-y-4"
                                >
                                    <h2 className="text-3xl font-black text-white tracking-tight">Blueprint Exported</h2>
                                    <p className="text-blue-200 text-lg">Your physical training companion is ready.</p>
                                    <div className="flex items-center justify-center gap-2 text-green-400 font-bold">
                                        <Printer className="w-5 h-5" />
                                        <span>Plan Printed</span>
                                    </div>
                                </motion.div>
                            </div>
                        )}

                        {/* Particle Effects - using pre-computed values */}
                        <div className="absolute inset-0 pointer-events-none">
                            {particleConfigs.map((config, i) => (
                                <motion.div
                                    key={i}
                                    initial={{
                                        x: config.initialX,
                                        y: config.initialY,
                                        opacity: 0
                                    }}
                                    animate={{
                                        y: config.animateY,
                                        opacity: [0, 1, 0],
                                        scale: [0, 1.5, 0]
                                    }}
                                    transition={{
                                        duration: config.duration,
                                        repeat: Infinity,
                                        delay: config.delay
                                    }}
                                    className="absolute left-1/2 top-1/2 w-1 h-1 bg-blue-300 rounded-full"
                                />
                            ))}
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
